import React from 'react'
import { HashRouter, Route, Switch } from 'react-router-dom'
import './scss/style.scss'
import { AppThemeProvider } from './theme/ThemeContext'
import { AuthStateAndStatus, AuthenticationProvider, AuthContext } from './api/authentication/AuthenticationContext'
import { FeatureFlagProvider } from './api/feature-flags/FeatureFlagContext'
import useAuthContext from './api/authentication/useAuthContext'
import AmplitudePageTracker from './AmplitudePageTracker'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)

// Containers
const TheLayout = React.lazy(() => import('./layout/TheLayout'))

// Pages
const Login = React.lazy(() => import('./layout/login/Login'))
const ForgotPassword = React.lazy(() => import('./layout/login/forgot-password/ForgotPassword'))
const EmailUnsubscribed = React.lazy(() => import('./layout/login/email-unsubscribed/EmailUnsubscribed'))
const ForgotPasswordInitial = React.lazy(() => import('./layout/login/forgot-password/ForgotPasswordInitial'))
const CreateAccount = React.lazy(() => import('./layout/login/create-account/CreateAccount'))
const SetNewPassword = React.lazy(() => import('./layout/login/set-new-password/SetNewPassword'))
const SetNewPin = React.lazy(() => import('./layout/login/set-new-pin/SetNewPin'))
const SsoComplete = React.lazy(() => import('./layout/login/sso/SsoComplete'))

const AuthenticatedFeatureFlags: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuthContext()
  return <FeatureFlagProvider enabled={authState.currentContext === AuthContext.loggedIn}>{children}</FeatureFlagProvider>
}

const location = window.location
const landingPath = location.hash.replace('#', '')

type AppType = {
  initialAuthState: AuthStateAndStatus
}

const App: React.FC<AppType> = ({ initialAuthState }) => {
  return (
    <AppThemeProvider>
      <HashRouter>
        <React.Suspense fallback={loading}>
          <AuthenticationProvider initialAuthState={initialAuthState}>
            <AuthenticatedFeatureFlags>
            <AmplitudePageTracker />
            <Switch>
              <Route exact path="/email-unsubscribed" component={EmailUnsubscribed} />
              <Route exact path="/forgot-password" component={ForgotPassword} />
              <Route exact path="/initial-password" component={ForgotPasswordInitial} />
              <Route exact path="/create-account" component={CreateAccount} />
              <Route exact path="/set-new-password" component={SetNewPassword} />
              <Route exact path="/set-new-pin" component={SetNewPin} />
              <Route exact path="/sso/complete" component={SsoComplete} />
              <Route exact path="/login" render={(props) => <Login {...props} landingPath={landingPath} />} />
              <Route path="/" component={TheLayout} />
            </Switch>
            </AuthenticatedFeatureFlags>
          </AuthenticationProvider>
        </React.Suspense>
      </HashRouter>
    </AppThemeProvider>
  )
}

export default App